import type {
  StaffingChangeSummary,
  StaffingChangeType,
  StaffingReference,
  StaffingWorkerLifecycleType,
} from '../operations/model';
import type { StatusTone } from '../../../design-system';

export type ChangeType = StaffingChangeType;
export type StatusFilter = '' | 'DRAFT' | 'IN_PROGRESS' | 'SCHEDULED' | 'COMPLETED' | 'DENIED' | 'CANCELLED';
export type DraftReference = {
  id: string;
  processType: StaffingWorkerLifecycleType | 'JOB_CHANGE';
  workflow?: StaffingReference;
};

export type StaffingRow = {
  id: string;
  worker: string;
  workerNumber: string;
  process: string;
  summary: string;
  effectiveDate: string;
  activityDate: string;
  activityLabel: string;
  status: string;
  source: StaffingChangeSummary;
};

export const clearWorkSpaceValue = '__CLEAR__';

export const timeTypeLabels = {
  FULL_TIME: '전일제',
  PART_TIME: '시간제',
} as const;

export const processLabels: Record<string, string> = {
  HIRE: '입사',
  JOB_CHANGE: '조직·직무 변경',
  LEAVE_OF_ABSENCE: '휴직',
  RETURN_FROM_LEAVE: '복직',
  END_WORKER: '퇴직·계약 종료',
};

export function processLabel(value: string) {
  return processLabels[value] ?? value;
}

export function subjectLabel(change: StaffingChangeSummary) {
  return change.worker?.descriptor || change.person?.descriptor || change.subject.descriptor || '이름 미등록';
}

export function formatDateOnly(value?: string) {
  return value ? value.slice(0, 10).replaceAll('-', '.') : '-';
}

export function formatInstantDate(value?: string) {
  if (!value) return '-';
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return '-';
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(instant).replaceAll('-', '.');
}

export function addDateOnlyDays(value: string, days: number) {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return date.toISOString().slice(0, 10);
}

export function isScheduled(change: StaffingChangeSummary, today: string) {
  return change.status === 'COMPLETED' && Boolean(change.effectiveDate && change.effectiveDate > today);
}

export function statusPresentation(change: StaffingChangeSummary, today: string): { label: string; tone: StatusTone } {
  if (change.status === 'DRAFT') return { label: '초안', tone: 'warning' };
  if (change.status === 'IN_PROGRESS') return { label: '승인 중', tone: 'info' };
  if (isScheduled(change, today)) return { label: '시행 대기', tone: 'success' };
  if (change.status === 'COMPLETED') return { label: '완료', tone: 'success' };
  if (change.status === 'DENIED') return { label: '반려', tone: 'danger' };
  if (change.status === 'CANCELLED') return { label: '취소', tone: 'neutral' };
  return { label: change.status, tone: 'neutral' };
}

export function matchesStatus(change: StaffingChangeSummary, filter: StatusFilter, today: string) {
  if (!filter) return true;
  if (filter === 'SCHEDULED') return isScheduled(change, today);
  if (filter === 'COMPLETED') return change.status === 'COMPLETED' && !isScheduled(change, today);
  return change.status === filter;
}

export function progressTone(
  change: StaffingChangeSummary,
  step: 'DRAFT' | 'APPROVAL' | 'EFFECTIVE',
  today: string,
): 'neutral' | 'active' | 'success' | 'danger' {
  if (step === 'DRAFT') return change.status === 'DRAFT' ? 'active' : change.status === 'CANCELLED' ? 'neutral' : 'success';
  if (step === 'APPROVAL') {
    if (change.status === 'IN_PROGRESS') return 'active';
    if (change.status === 'DENIED') return 'danger';
    if (change.status === 'COMPLETED') return 'success';
    return 'neutral';
  }
  if (isScheduled(change, today)) return 'active';
  if (change.status === 'COMPLETED') return 'success';
  return 'neutral';
}

export function progressDescription(
  change: StaffingChangeSummary,
  step: 'DRAFT' | 'APPROVAL' | 'EFFECTIVE',
  today: string,
) {
  if (step === 'DRAFT') return formatInstantDate(change.createdAt);
  if (step === 'APPROVAL') {
    if (change.status === 'DRAFT') return '승인 요청 전';
    if (change.status === 'DENIED') return '반려됨';
    if (change.status === 'CANCELLED') return '취소됨';
    if (change.status === 'IN_PROGRESS') return '결재 진행 중';
    return formatInstantDate(change.completedAt);
  }
  if (change.status === 'DENIED') return '반려되어 미시행';
  if (change.status === 'CANCELLED') return '취소되어 미시행';
  if (isScheduled(change, today)) return `${formatDateOnly(change.effectiveDate)} 시행 대기`;
  if (change.status === 'COMPLETED') return `${formatDateOnly(change.effectiveDate)} 반영`;
  return `${formatDateOnly(change.effectiveDate)} 예정`;
}

export function lifecycleAfterLabel(type: ChangeType) {
  if (type === 'LEAVE_OF_ABSENCE') return '휴직';
  if (type === 'RETURN_FROM_LEAVE') return '재직';
  if (type === 'END_WORKER') return '퇴직·계약 종료';
  return '';
}

export function errorStatus(failure: unknown) {
  return failure && typeof failure === 'object' && 'status' in failure && typeof failure.status === 'number'
    ? failure.status
    : undefined;
}

export function errorCode(failure: unknown) {
  return failure && typeof failure === 'object' && 'code' in failure && typeof failure.code === 'string'
    ? failure.code
    : undefined;
}
